import dayjs from 'dayjs';
import type { RateInputs, FormRateInputs, InvestorType } from '../types/rates';

// Default values configuration - undefined means no default
export const defaultValues: Partial<RateInputs> = {
  investorType: undefined,
  lifeInsuredGender: undefined,
  lifeInsuredAge: undefined,
  hasSpouse: undefined,
  spouseGender: undefined,
  spouseAge: undefined,
  agePensionPlus: undefined,
  paymentType: undefined,
  youngerSpouse: undefined
};
// For testing
// export const defaultValues: Partial<RateInputs> = {
//   investorType: 'new',
//   lifeInsuredGender: 'male',
//   lifeInsuredAge: 65,
//   hasSpouse: false,
//   spouseGender: undefined,
//   spouseAge: undefined,
//   agePensionPlus: false,
//   paymentType: 'fixed',
//   youngerSpouse: undefined
// };

// Validation ranges configuration
export const validationValues = {
  lifeInsuredAgeMin: 50,
  lifeInsuredAgeMax: 80,
  spouseAgeMin: 50,
  spouseAgeMax: 80,
  deferralPeriodMinExisting: [
    { date: '2026-01-19', min: 1 },
    { date: '2023-03-01', min: 3 }
  ],
  deferralPeriodMinNew: [
    { date: '2023-03-01', min: 1 }
  ],
  ageAgileIncomeStartsMax: 100, // Note this is validated on (deferralPeriod + lifeInsuredAge) =< ageAgileIncomeStartsMax
  investmentDateMin: '2023-03-01',
  investmentDateMax: dayjs().format('YYYY-MM-DD'),
  disableOptionsPriorTo: '2023-11-24'
};

// Shared function to get minimum deferral period based on investment date
export function getMinimumDeferralPeriod(investmentDate: string | undefined, investorType: InvestorType): number {
  const policyDate = new Date(investmentDate || 1);
  
  const deferralPeriodMinByInvestorType = investorType === 'new'
    ? validationValues.deferralPeriodMinNew
    : validationValues.deferralPeriodMinExisting;

  // Find the first entry where investmentDate is on or after the date
  for (const rule of deferralPeriodMinByInvestorType) {
    if (policyDate >= new Date(rule.date)) {
      return rule.min;
    }
  }
  
  // Fallback to the last entry's min value
  return deferralPeriodMinByInvestorType[deferralPeriodMinByInvestorType.length - 1].min;
}

// Shared function to get maximum deferral period
export function getMaximumDeferralPeriod(lifeInsuredAge: number): number {
  return validationValues.ageAgileIncomeStartsMax - lifeInsuredAge;
}

// Helper function to convert API defaults to form-safe defaults
export function getFormDefaults(apiDefaults: Partial<RateInputs> = defaultValues): Partial<FormRateInputs> {
  const defaultInvestmentDate = dayjs().format('YYYY-MM-DD')
  return {
    // Always provide controlled values - never undefined for form inputs
    investorType: apiDefaults.investorType ?? '',
    investmentDate: defaultInvestmentDate,
    lifeInsuredGender: apiDefaults.lifeInsuredGender || '',
    lifeInsuredAge: apiDefaults.lifeInsuredAge || 0,
    hasSpouse: apiDefaults.hasSpouse ?? '',
    spouseGender: apiDefaults.spouseGender || '',
    spouseAge: apiDefaults.spouseAge || 0,
    agePensionPlus: apiDefaults.agePensionPlus ?? '',
    paymentType: apiDefaults.paymentType || '',
    deferralPeriod: getMinimumDeferralPeriod(defaultInvestmentDate, apiDefaults.investorType || 'new'),
    youngerSpouse: apiDefaults.youngerSpouse || ''
  };
}

// Helper function to calculate default agile income start age based on AC11
// export function calculateDefaultAgileIncomeStartAge(
//   investmentDate: string | undefined,
//   lifeInsuredAge: number
// ): number {
//   const deferralPeriod = calculateDefaultDeferralPeriod(investmentDate);
//   return lifeInsuredAge + deferralPeriod;
// }

// // Helper function to calculate default deferral period based on AC11
// export function calculateDefaultDeferralPeriod(
//   investmentDate: string | undefined
// ): number {
//   return getMinimumDeferralPeriod(investmentDate);
// }